import { useMemo, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchableNavMenu } from 'components/MapPage/SearchableNavMenu';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { isHardcodedMemphisThreatNotification } from 'components/MapPage/CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import { useUserNotifications } from 'hooks/useUserNotifications';
import {
  useHighSecurityFlag,
  useHwcgFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { useActiveSeatNav } from 'components/IAP/positionWorkspace/useActiveSeats';
import {
  MY_ASSIGNMENT_NONE_MESSAGE,
  myAssignmentSlug,
} from 'components/IAP/positionWorkspace/myAssignment';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import { openIncidentMapTab } from 'components/IAP/openIncidentMapTab';
import {
  Box,
  Calendar,
  Flag,
  Folder,
  Layers,
  Library,
  ListChecks,
  ClipboardCheck,
  ClipboardList,
  Megaphone,
  NotebookPen,
  Presentation,
  Radio,
  Users,
} from 'lucide-react';

type MoreDestination = {
  id: string;
  label: string;
  icon: ReactNode;
  path?: string;
  openTab?: string;
};

const ROUTE_MORE: MoreDestination[] = [
  {
    id: 'actions',
    label: 'Tasks',
    path: 'actions',
    icon: <ListChecks className="tw:size-4" />,
  },
  {
    id: 'apply-plan',
    label: 'Apply Plan',
    path: 'apply-plan',
    icon: <Library className="tw:size-4" />,
  },
  {
    id: 'roster',
    label: 'Roster',
    path: 'roster',
    icon: <Users className="tw:size-4" />,
  },
  {
    id: 'resources',
    // Labelled Assets to match the hub. The route stays /resources.
    label: 'Assets',
    path: 'resources',
    icon: <Box className="tw:size-4" />,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    path: 'calendar',
    icon: <Calendar className="tw:size-4" />,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: 'reports',
    icon: <Flag className="tw:size-4" />,
  },
  {
    id: 'files',
    label: 'Files',
    path: 'files',
    icon: <Folder className="tw:size-4" />,
  },
  {
    id: 'presentation',
    label: 'Presentation',
    path: 'presentation',
    icon: <Presentation className="tw:size-4" />,
  },
  {
    id: 'notes',
    label: 'Notes',
    path: 'notes',
    icon: <NotebookPen className="tw:size-4" />,
  },
  {
    id: 'layers',
    label: 'Map Layers',
    openTab: 'Layers',
    icon: <Layers className="tw:size-4" />,
  },
];

function iapSubpath(pathname: string, incidentId: string): string {
  const prefix = `/iap/${incidentId}`;
  if (pathname === prefix || pathname === `${prefix}/`) return '';
  if (!pathname.startsWith(`${prefix}/`)) return '';
  return pathname.slice(prefix.length + 1);
}

const WorkspaceNavTab: React.FC<{
  to: string | { pathname: string; search: string };
  label: string;
  active: boolean;
}> = ({ to, label, active }) => (
  <Link to={to}>
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      className="tw:h-8"
    >
      {label}
    </Button>
  </Link>
);

const IncidentWorkspaceTabBar: React.FC<{
  endSlot?: ReactNode;
}> = ({ endSlot }) => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const highSecurityFlag = useHighSecurityFlag();
  const hwcg = useHwcgFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  const showApplyPlan = hwcg || uscgFoc;
  const { held } = useActiveSeatNav();
  const userGuid = useAppSelector(selectUserGuid);
  const { data: roster } = useGetRosterForIncident();
  const { isMapVisible, setIsMapVisible } = useUscgMapContext();
  const { data: userNotifications = [] } = useUserNotifications();

  const moreDestinations = useMemo((): MoreDestination[] => {
    let items = highSecurityFlag
      ? ROUTE_MORE.filter((item) => item.id !== 'actions')
      : ROUTE_MORE;
    if (!showApplyPlan) {
      items = items.filter((item) => item.id !== 'apply-plan');
    }
    const withGroupActions = hwcg
      ? [
          items[0],
          {
            id: 'review-queue',
            label: 'Review Queue',
            path: 'review-queue',
            icon: <ClipboardCheck className="tw:size-4" />,
          },
          {
            id: 'incident-specifics',
            label: 'Incident Specifics',
            path: 'incident-specifics',
            icon: <ClipboardList className="tw:size-4" />,
          },
          {
            id: 'group-actions',
            label: 'Group Actions',
            path: 'group-actions',
            icon: <Megaphone className="tw:size-4" />,
          },
          ...items.slice(1),
        ]
      : items;
    const mine =
      held.kind === 'one'
        ? {
            id: 'my-workspace',
            label: 'My Workspaces',
            path: `seat/${held.slug}`,
            icon: <Users className="tw:size-4" />,
          }
        : held.kind === 'many'
          ? {
              id: 'my-workspace',
              label: 'My Workspaces',
              path: '',
              icon: <Users className="tw:size-4" />,
            }
          : null;
    return [
      ...(mine ? [mine] : []),
      ...withGroupActions,
      {
        id: 'events',
        label: 'Events',
        openTab: 'Events',
        icon: <Radio className="tw:size-4" />,
      },
    ];
  }, [highSecurityFlag, held, hwcg, showApplyPlan]);

  const activeCount = useMemo(() => {
    if (!incidentId) return 0;
    return userNotifications.filter(
      (n) =>
        !n.archived &&
        (n.incidentId === incidentId ||
          isHardcodedMemphisThreatNotification(n.notificationId))
    ).length;
  }, [userNotifications, incidentId]);

  if (!incidentId) return null;

  const subpath = iapSubpath(location.pathname, incidentId);
  const searchParams = new URLSearchParams(location.search);
  const openTab = searchParams.get('openTab');
  const formsActive = subpath === '' || subpath === 'dashboard';
  const mapMoreOpen = openTab === 'Layers' || openTab === 'Events';
  const notificationsActive = subpath === 'notifications';
  const moreItem = moreDestinations.find((item) => {
    if (item.path) {
      return subpath === item.path || subpath.startsWith(`${item.path}/`);
    }
    return formsActive && openTab === item.openTab;
  });
  const formsSearch = new URLSearchParams(searchParams);
  formsSearch.delete('openTab');
  const formsTo = {
    pathname: `/iap/${incidentId}/dashboard`,
    search: formsSearch.toString() ? `?${formsSearch.toString()}` : '',
  };

  const openMyAssignment = () => {
    const slug = myAssignmentSlug(roster, userGuid);
    if (!slug) {
      toast.info(MY_ASSIGNMENT_NONE_MESSAGE);
      return;
    }
    navigate(`/iap/${incidentId}/seat/${slug}`);
  };

  const badgeLabel = activeCount > 99 ? '99+' : String(activeCount);
  const notificationsPath = `/iap/${incidentId}/notifications`;

  const openMapTab = (tab: string) => {
    openIncidentMapTab({
      tab,
      incidentId,
      pathname: location.pathname,
      search: location.search,
      navigate,
      setIsMapVisible,
    });
  };

  return (
    <div className="dark tw:flex tw:w-full tw:shrink-0 tw:items-center tw:gap-2 tw:border-b tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-foreground">
      <Link to={notificationsPath}>
        <OverlayTrigger
          placement="bottom"
          overlay={(props) => <Tooltip {...props}>Notifications</Tooltip>}
        >
          <Button
            type="button"
            size="icon-sm"
            variant={notificationsActive ? 'default' : 'outline'}
            className="tw:relative"
            aria-label={
              activeCount > 0
                ? `Notifications (${activeCount})`
                : 'Notifications'
            }
          >
            <SharedIcon iconName="notifications" size="sm" />
            {activeCount > 0 ? (
              <Badge
                variant="count-destructive"
                className="tw:pointer-events-none tw:absolute tw:-right-1 tw:-top-1 tw:h-4 tw:min-w-4 tw:px-1 tw:text-[10px]"
                aria-label={`${activeCount} active notifications`}
              >
                {badgeLabel}
              </Badge>
            ) : null}
          </Button>
        </OverlayTrigger>
      </Link>
      {isMapVisible ? null : (
        <WorkspaceNavTab
          to={formsTo}
          label="Forms"
          active={formsActive && !mapMoreOpen}
        />
      )}
      {hwcg ? (
        <Button
          type="button"
          size="sm"
          variant={subpath.startsWith('seat/') ? 'default' : 'outline'}
          className="tw:h-8"
          onClick={openMyAssignment}
        >
          My Assignment
        </Button>
      ) : null}
      <SearchableNavMenu
        id="incident-workspace-more-tabs"
        triggerLabel={moreItem ? `More · ${moreItem.label}` : 'More'}
        selectedId={moreItem?.id}
        triggerVariant={moreItem ? 'default' : 'outline'}
        items={moreDestinations.map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
        }))}
        onSelect={(item) => {
          const dest = moreDestinations.find((entry) => entry.id === item.id);
          if (!dest) return;
          if (dest.openTab) {
            openMapTab(dest.openTab);
            return;
          }
          if (dest.path !== undefined) {
            const suffix = dest.path ? `/${dest.path}` : '';
            navigate(`/iap/${incidentId}${suffix}`);
          }
        }}
      />
      {endSlot ? (
        <div className="tw:ml-auto tw:flex tw:items-center">{endSlot}</div>
      ) : null}
    </div>
  );
};

export default IncidentWorkspaceTabBar;
